/**
 * ComuniManager — sezione collassabile per gestire le configurazioni
 * dei Comuni (nome, tariffa, max notti per soggiorno, età esenzione).
 * Le configurazioni vengono usate per calcolare l'imposta di soggiorno
 * sui report caricati.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Buildings, Pencil, Trash, Plus, FloppyDisk, X, Info, CaretDown, CaretUp } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORM_DEFAULT = { name: '', tax_per_night: 3.0, max_nights: 5, exemption_age: 18 };

export default function ComuniManager({ onChange }) {
  const [comuni, setComuni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [editingId, setEditingId] = useState(null);  // null = nessun editor, "new" = nuovo, ObjectId = modifica
  const [form, setForm] = useState(FORM_DEFAULT);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/comuni`, { withCredentials: true });
      setComuni(r.data.items || []);
      onChange?.(r.data.items || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/comuni`, { withCredentials: true });
        if (!mounted) return;
        setComuni(r.data.items || []);
        onChange?.(r.data.items || []);
      } catch {/* ignore */}
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  const startNew = () => {
    setForm(FORM_DEFAULT);
    setEditingId('new');
  };
  const startEdit = (c) => {
    setForm({
      name: c.name,
      tax_per_night: c.tax_per_night,
      tax_per_night_text: String(c.tax_per_night).replace('.', ','),
      max_nights: c.max_nights,
      exemption_age: c.exemption_age,
    });
    setEditingId(c.id);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setForm(FORM_DEFAULT);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Nome del Comune obbligatorio'); return; }
    setSaving(true);
    // Strip helper field before sending to backend
    const { tax_per_night_text: _drop, ...payload } = form;
    try {
      if (editingId === 'new') {
        await axios.post(`${API}/comuni`, payload, { withCredentials: true });
        toast.success('Comune aggiunto');
      } else {
        await axios.put(`${API}/comuni/${editingId}`, payload, { withCredentials: true });
        toast.success('Comune aggiornato');
      }
      cancelEdit();
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare la configurazione di ${c.name}?`)) return;
    try {
      await axios.delete(`${API}/comuni/${c.id}`, { withCredentials: true });
      toast.success('Comune eliminato');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const exemptionLabel = (age) => age >= 18 ? 'Tutti i minori esenti' : `Minori di ${age} anni esenti`;
  const maxNightsLabel = (n) => n === 0 ? 'Nessun limite' : `${n} notti max per soggiorno`;

  return (
    <div className="bg-white border border-zinc-200 mb-6" data-testid="comuni-manager">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 sm:p-5 hover:bg-zinc-50 transition-colors"
        data-testid="comuni-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-sky-700 text-white flex items-center justify-center">
            <Buildings size={18} weight="bold" />
          </div>
          <div className="text-left">
            <p className="text-sm font-black text-zinc-900 tracking-tight">Configurazioni Comuni</p>
            <p className="text-[11px] text-zinc-500">{comuni.length === 0 ? 'Nessun Comune configurato' : `${comuni.length} ${comuni.length === 1 ? 'Comune' : 'Comuni'} salvat${comuni.length === 1 ? 'o' : 'i'}`}</p>
          </div>
        </div>
        {open ? <CaretUp size={14} weight="bold" className="text-zinc-400" /> : <CaretDown size={14} weight="bold" className="text-zinc-400" />}
      </button>

      {open && (
        <div className="border-t border-zinc-100 p-4 sm:p-5">
          {/* Lista */}
          {comuni.length > 0 && (
            <div className="space-y-2 mb-4">
              {comuni.map((c) => (
                <div key={c.id} className="flex items-center justify-between p-3 border border-zinc-200 hover:border-zinc-300 transition-colors" data-testid={`comune-row-${c.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-black text-zinc-900 truncate">{c.name}</p>
                    <p className="text-[11px] text-zinc-500">
                      €{c.tax_per_night.toFixed(2)}/notte · {maxNightsLabel(c.max_nights)} · {exemptionLabel(c.exemption_age)}
                    </p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => startEdit(c)} className="w-8 h-8 hover:bg-zinc-100 flex items-center justify-center text-zinc-600 hover:text-zinc-900" data-testid={`comune-edit-${c.id}`}><Pencil size={13} weight="bold" /></button>
                    <button onClick={() => remove(c)} className="w-8 h-8 hover:bg-red-50 flex items-center justify-center text-zinc-400 hover:text-red-600" data-testid={`comune-delete-${c.id}`}><Trash size={13} weight="bold" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Nuovo / Editor */}
          {editingId ? (
            <div className="bg-zinc-50 border border-zinc-200 p-4 space-y-3" data-testid="comune-form">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-black uppercase tracking-wider text-zinc-600">{editingId === 'new' ? 'Nuovo Comune' : 'Modifica Comune'}</p>
                <button onClick={cancelEdit} className="text-zinc-400 hover:text-zinc-700" data-testid="comune-form-cancel"><X size={14} weight="bold" /></button>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">Nome del Comune</label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Es. Milano" className="w-full h-9 px-3 border border-zinc-300 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 outline-none text-sm" data-testid="comune-name-input" />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">Tariffa €/notte</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={form.tax_per_night_text ?? String(form.tax_per_night).replace('.', ',')}
                    onChange={e => {
                      // Accept both comma and dot, plus blank intermediate state
                      const raw = e.target.value.replace(/[^\d.,]/g, '');
                      const numeric = parseFloat(raw.replace(',', '.'));
                      setForm({
                        ...form,
                        tax_per_night_text: raw,
                        tax_per_night: isNaN(numeric) ? 0 : numeric,
                      });
                    }}
                    onBlur={e => {
                      // Normalize display on blur: "1,5" stays "1,5"
                      const numeric = parseFloat(e.target.value.replace(',', '.'));
                      setForm({
                        ...form,
                        tax_per_night_text: isNaN(numeric) ? '0' : String(numeric).replace('.', ','),
                        tax_per_night: isNaN(numeric) ? 0 : numeric,
                      });
                    }}
                    placeholder="es. 1,5"
                    className="w-full h-9 px-3 border border-zinc-300 text-sm tabular-nums"
                    data-testid="comune-tax-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">Max notti / soggiorno</label>
                  <input type="number" step="1" min="0" max="60" value={form.max_nights} onChange={e => setForm({ ...form, max_nights: Number(e.target.value) || 0 })} className="w-full h-9 px-3 border border-zinc-300 text-sm tabular-nums" data-testid="comune-maxnights-input" />
                  <p className="text-[10px] text-zinc-400 mt-1">0 = nessun limite</p>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">Età esenzione</label>
                  <select value={form.exemption_age} onChange={e => setForm({ ...form, exemption_age: Number(e.target.value) })} className="w-full h-9 px-3 border border-zinc-300 text-sm" data-testid="comune-exemption-select">
                    <option value={18}>Tutti i minori</option>
                    <option value={14}>Minori di 14</option>
                    <option value={12}>Minori di 12</option>
                    <option value={10}>Minori di 10</option>
                    <option value={6}>Minori di 6</option>
                    <option value={2}>Minori di 2</option>
                    <option value={0}>Nessuna esenzione</option>
                  </select>
                </div>
              </div>

              <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 p-2.5">
                <Info size={13} weight="bold" className="text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-blue-900 leading-snug">
                  Esempi: <strong>Milano</strong> €5/notte, max 5, tutti minori esenti · <strong>Roma</strong> €4-7/notte, max 10, minori di 10 · <strong>Firenze</strong> €4/notte, max 7, minori di 12.
                </p>
              </div>

              <div className="flex gap-2 justify-end pt-1">
                <Button onClick={cancelEdit} variant="outline" className="rounded-none border-zinc-300 text-xs font-bold uppercase tracking-wider h-8" data-testid="comune-form-cancel-btn">Annulla</Button>
                <Button onClick={save} disabled={saving} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-bold uppercase tracking-wider h-8" data-testid="comune-form-save-btn">
                  <FloppyDisk size={12} weight="bold" className="mr-1" /> {saving ? 'Salvataggio...' : 'Salva'}
                </Button>
              </div>
            </div>
          ) : (
            <Button onClick={startNew} variant="outline" className="rounded-none border-dashed border-zinc-300 hover:border-zinc-900 text-xs font-bold uppercase tracking-wider w-full h-10" data-testid="comune-new-btn">
              <Plus size={13} weight="bold" className="mr-2" /> Aggiungi Comune
            </Button>
          )}

          {!loading && comuni.length === 0 && !editingId && (
            <p className="text-center text-xs text-zinc-400 mt-4">
              Configura almeno un Comune per ottenere il calcolo automatico dell&apos;imposta dovuta sui report caricati.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
